/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    getFilterElementsDefault,
    sharpDateFormatter,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { getLeadgenHourlyStats, getLeadgenHourlyStatsExcel } from 'axios/leadgenApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import { subDays } from 'date-fns';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { groupBy as rowGrouper } from 'lodash';
import {
    LeadgenFilter as LeadgenFilterType,
    LeadgenHourlyStat,
    LeadgenInfo,
} from 'models/api/leadgen';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useCallback, useState } from 'react';
import { FormatterProps, GroupFormatterProps, SummaryFormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import { useQueryClient } from 'react-query';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import { LeadgenProps } from './Leadgen';
import './Leadgen.scss';
import { LeadgenHourlyStatsFilter } from './LeadgenFilter';

type LeadgenHourlyStatsFormatterProps = FormatterProps<LeadgenHourlyStat, LeadgenHourlyStat[]>;

type LeadgenHourlyStatsGroupFormatterProps = GroupFormatterProps<
    LeadgenHourlyStat,
    LeadgenHourlyStat[]
>;

type LeadgenHourlyStatsSummaryFormatterProps = SummaryFormatterProps<
    LeadgenHourlyStat[],
    LeadgenHourlyStat
>;

const LeadgenHourlyStats = (props: LeadgenProps) => {
    const api = useApi();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { dateTimeFormat, dateFormat, timeFormat } = useGeneralSettingsContext();
    const { hasRole } = useAuth();
    const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>();

    const queryKey = ['LeadgenHourlyStats'];

    const groupFormatter = useCallback((key: keyof LeadgenHourlyStat) => {
        const component = ({ row: { childRows } }: LeadgenHourlyStatsGroupFormatterProps) => (
            <>{childRows[0][key]}</>
        );
        return component;
    }, []);

    const summaryFormatter = useCallback((key: keyof LeadgenHourlyStat) => {
        const component = ({ row }: LeadgenHourlyStatsSummaryFormatterProps) => <>{row[0][key]}</>;
        return component;
    }, []);

    const dtGroupFormatter = useCallback(
        ({ childRows, isExpanded }: LeadgenHourlyStatsGroupFormatterProps) => (
            <>
                {isExpanded ? <FaAngleDown /> : <FaAngleRight />}
                <DataTimeText value={childRows[0].dt} format={dateFormat} />
            </>
        ),
        []
    );

    const dtSummaryFormatter = useCallback(() => <>{t('BackOfficeTotal')}:</>, []);

    const leadgentimeFormatter = useCallback(
        ({ row }: LeadgenHourlyStatsFormatterProps) => (
            <>{sharpDateFormatter(row.time, timeFormat)}</>
        ),
        []
    );

    return (
        <BOWorkplace<LeadgenHourlyStat, LeadgenInfo, LeadgenFilterType>
            testId="leadgen-hourly-stats"
            filterParams={{
                sd: getStartOfTheDay(subDays(new Date(), 7)),
                ed: getEndOfTheDay(),
                showInactive: false,
                ids: { value: [], array: true },
            }}
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(
                            val.toString(),
                            dateTimeFormat
                        )}`,
                },
                {
                    id: 'ed',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(
                            val.toString(),
                            dateTimeFormat
                        )}`,
                },
                {
                    id: 'ids',
                    name: t('BackOfficeLeadgens'),
                    formatter: (val) => {
                        if (typeof val == 'object') {
                            if (!props.leadgenListLoading) {
                                return val
                                    .map(
                                        (i) =>
                                            props.leagensList.find((j) => j.id === i.toString())
                                                ?.name
                                    )
                                    .join(', ');
                            } else {
                                return val.join(', ');
                            }
                        }
                        return '';
                    },
                },
                {
                    id: 'showInactive',
                    formatter: (val) => {
                        if (val) {
                            return t('BackOfficeShowInactive');
                        }
                        return '';
                    },
                },
            ])}
            fetchExcelQueryFn={(filter) => getLeadgenHourlyStatsExcel(api, filter)}
            buttons={[]}
            showExcelBtn={hasRole('leadgenGetHourlyStatsExcel')}
            filter={<LeadgenHourlyStatsFilter {...props} />}
            fetchQueryFn={(filter) => getLeadgenHourlyStats(api, filter)}
            queryKey={queryKey}
            queryClient={queryClient}
            pageTitle="BackOfficeLeadgen"
            storagePath="LeadgenHourlyStats/columns"
            showSummaryRows
            urlFilterKeyPrefix="LeadgenHourlyStats"
            groupBy={['dt']}
            rowGrouper={rowGrouper}
            expandedGroupIds={expandedGroupIds}
            onExpandedGroupIdsChange={setExpandedGroupIds}
            initColumns={[
                {
                    key: 'dt',
                    name: <Trans i18nKey="BackOfficeDate" />,
                    groupFormatter: dtGroupFormatter,
                    summaryCellClass: 'leadgen__table-summary leadgen__table-summary-header-column',
                    summaryFormatter: dtSummaryFormatter,
                },
                {
                    key: 'time',
                    formatter: leadgentimeFormatter,
                    name: <Trans i18nKey="BackOfficeTime" />,
                    summaryCellClass: 'leadgen__table-summary leadgen__table-summary-header-column',
                    sortable: false,
                },
                {
                    key: 'clickCount',
                    name: <Trans i18nKey="BackOfficeLeadgenClicks" />,
                    summaryFormatter: summaryFormatter('clickCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('clickCountDt'),
                    sortable: false,
                },
                {
                    key: 'ordersAllCount',
                    name: <Trans i18nKey="BackOfficeOrders" />,
                    summaryFormatter: summaryFormatter('ordersAllCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('ordersAllCountDt'),
                    sortable: false,
                },
                {
                    key: 'ordersApprovedCount',
                    name: <Trans i18nKey="BackOfficeApproved" />,
                    summaryFormatter: summaryFormatter('ordersApprovedCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'ordersGoodshipCount',
                    name: <Trans i18nKey="BackOfficeGoodship" />,
                    summaryFormatter: summaryFormatter('ordersGoodshipCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('ordersGoodshipCountDt'),
                    sortable: false,
                },
                {
                    key: 'ordersDeclinedCount',
                    name: <Trans i18nKey="BackOfficeDeclined" />,
                    summaryFormatter: summaryFormatter('ordersDeclinedCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('ordersDeclinedCountDt'),
                    sortable: false,
                },
                {
                    key: 'appsNew',
                    name: <Trans i18nKey="BackOfficeAppsNew" />,
                    summaryFormatter: summaryFormatter('appsNewTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('appsNewDt'),
                    sortable: false,
                },
                {
                    key: 'appsNewToAllOrdersDt',
                    name: <Trans i18nKey="BackOfficeAppsNewToAllOrders" />,
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('appsNewToAllOrdersDt'),
                    sortable: false,
                    tooltipText: t('BackOfficeAppsNewToAllOrdersNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'loansToAppsNewDt',
                    name: <Trans i18nKey="BackOfficeLoansToAppsNew" />,
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('loansToAppsNewDt'),
                    sortable: false,
                    tooltipText: t('BackOfficeLoansToAppsNewNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'approvedToAppsNewDt',
                    name: <Trans i18nKey="BackOfficeApprovedToAppsNew" />,
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('approvedToAppsNewDt'),
                    sortable: false,
                    tooltipText: t('BackOfficeApprovedToAppsNewNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'loansToApprovedDt',
                    name: <Trans i18nKey="BackOfficeLoansToApproved" />,
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('loansToApprovedDt'),
                    sortable: false,
                    tooltipText: t('BackOfficeLoansToApprovedNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'loansToClicksDt',
                    name: <Trans i18nKey="BackOfficeLoansToClicks" />,
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('loansToClicksDt'),
                    sortable: false,
                    tooltipText: t('BackOfficeLoansToClicksNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'postbackSent',
                    name: <Trans i18nKey="BackOfficePostbackSent" />,
                    summaryFormatter: summaryFormatter('postbackSentTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('postbackSentDt'),
                    sortable: false,
                },
                {
                    key: 'tariffAmount',
                    name: <Trans i18nKey="BackOfficeTariffAmount" />,
                    summaryFormatter: summaryFormatter('tariffAmountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                    groupFormatter: groupFormatter('tariffAmountDt'),
                    sortable: false,
                },
            ]}
        />
    );
};

const useLeadgenHourlyStatsWorkplaceState = () =>
    useWorkplaceState<LeadgenHourlyStat, LeadgenInfo>();

export { useLeadgenHourlyStatsWorkplaceState };
export default LeadgenHourlyStats;
